import { writable, derived } from "svelte/store"

function alpha(hex, alpha) {
  if (!hex) return "rgba(0, 0, 0, 0)"
  let bigint = parseInt(hex.replace("#", ""), 16)
  let r = (bigint >> 16) & 255
  let g = (bigint >> 8) & 255
  let b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function lighten(hex) {
  return alpha(hex, 0.2)
}

export const partner = writable(null)
export const partnerId = writable(null)
export const primaryColor = writable(null)
export const primaryColorLighter = derived(primaryColor, lighten)
export const primaryColorAccent = writable(null)
export const logoAlt = writable(null)
export const logoPath = writable(null)
export const landingHeroPath = writable(null)
export const landingHeroMobilePath = writable(null)
export const landingHeroBgColor = writable(null)
export const landingHeroLayerBgColor = writable(null)
export const landingSectionConveniencePath = writable(null)
export const bgColor = writable(null)
export const bgPatternPath = writable(null)
export const topBar = writable(false)
export const barColor = writable(null)
export const topBarOffset = writable(null)
export const leftTopBarMessage = writable("")
export const rightTopBarMessage = writable("")
export const hideLanguageSelector = writable(false)
export const languages = writable([])

export const certificationPath = writable(null)
export const bgFunnelPath = writable(null)

export const infoSectionFontColor = writable(null)
export const linkSectionFontColor = writable(null)

export const loginDisabled = writable(false)
export const loginPageTitle = writable(null)
export const loginPageSubTitle = writable(null)
export const externalLogin = writable(false)
export const externalLoginCTA = writable(null)
export const externalLoginCTAUrl = writable(null)

export const registrationDisabled = writable(false)
export const registrationInfoPath = writable(null)
export const registrationHeroPath = writable(null)
export const registrationPageTitle = writable(null)
export const registrationPageSubTitle = writable(null)
export const registrationPageShowInfoBox = writable(null)
export const registrationPageShowBanner = writable(null)
export const registrationInfoBoxHeader = writable(null)
export const registrationInfoBoxDescription = writable(null)

const imgixParams = {
  auto: 'format',
  'min-w': 301,
  fit: 'crop',
  crop: 'entropy',
}

function partnerFileUrl(path) {
  return `https://SVELTE_APP_IMGIX_BASE_PATH/partners/${path}?${new URLSearchParams(imgixParams).toString()}`
}

function referralFileUrl(path) {
  return `https://SVELTE_APP_IMGIX_BASE_PATH/referrals/${path}?${new URLSearchParams(imgixParams).toString()}`
}

export function setPartner(_partner, referral = null) {
  if (_partner) {
    partner.set(_partner)
    partnerId.set(_partner._id.toString())
    primaryColor.set(_partner.origin.theme.primaryColor)
    primaryColorAccent.set(_partner.origin.theme.primaryColorAccent)
    logoAlt.set(_partner.origin.theme.logoAlt)
    logoPath.set(
      partnerFileUrl(`${_partner._id}/${_partner.origin.subdomain}/logo`)
    )
    landingHeroPath.set(
      `url(${partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/landingHero`
      )})`
    )
    landingHeroMobilePath.set(
      `url(${partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/landingHeroMobile`
      )})`
    )
    landingHeroBgColor.set(_partner.origin.theme.landingHeroBgColor)
    bgColor.set(_partner.origin.theme.bgColor)
    bgPatternPath.set(
      `url(${partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/bgPattern`
      )})`
    )
    topBar.set(_partner.origin.theme.topBar)
    topBarOffset.set(_partner.origin.theme.topBar ? "6rem" : "4rem")
    barColor.set(_partner.origin.theme.barColor ? _partner.origin.theme.barColor : "black")
    leftTopBarMessage.set(_partner.origin.theme.leftTopBarMessage)
    rightTopBarMessage.set(_partner.origin.theme.rightTopBarMessage)
    hideLanguageSelector.set(!!_partner.origin.theme.hideLanguageSelector)
    languages.set(_partner.props.languages || [])

    registrationHeroPath.set(
      `url(${partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/registrationHero`
      )})`
    )
    registrationInfoPath.set(
      partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/registrationInfo`
      )
    )
    certificationPath.set(
      partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/certification`
      )
    )
    landingSectionConveniencePath.set(
      `url(${partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/landingSectionConvenience`
      )})`
    )
    landingHeroLayerBgColor.set(_partner.origin.theme.landingHeroLayerBgColor)

    infoSectionFontColor.set(_partner.origin.theme.infoSectionFontColor)
    linkSectionFontColor.set(_partner.origin.theme.linkSectionFontColor)
    bgFunnelPath.set(
      `url(${partnerFileUrl(
        `${_partner._id}/${_partner.origin.subdomain}/bgFunnel`
      )})`
    )

    registrationDisabled.set(_partner.origin.isRegistrationDisabled)
    if (_partner.origin.registrationPage) {
      buildRegistrationPage(_partner.origin.registrationPage)
    }

    loginDisabled.set(_partner.origin.isLoginDisabled)
    if (_partner.origin.loginPage) {
      buildLoginPage(_partner.origin.loginPage)
    }
  }

  if (referral) {
    if (referral.props.theme) {
      const {
        logoImage: refLogoImage,
        logoAlt: refLogoAlt,
        landingHeroImg: refLandingHeroImg,
        landingHeroLayerBgColor: refLandingHeroLayerBgColor,
      } = referral.props.theme

      if (refLandingHeroImg) {
        landingHeroPath.set(
          `url(${referralFileUrl(`${referral._id}/landingHero`)})`
        )
      }
      if (refLogoAlt) {
        logoAlt.set(refLogoAlt)
      }
      if (refLogoImage) {
        logoPath.set(referralFileUrl(`${referral._id}/logo`))
      }
      if (refLandingHeroLayerBgColor) {
        landingHeroLayerBgColor.set(refLandingHeroLayerBgColor)
      }
    }

    if (referral.props.registrationPage?.showBanner) {
      registrationInfoPath.set(`${referralFileUrl(`${referral._id}/registrationInfo`)}`
      )
    }

    registrationDisabled.set(!!referral.props.isRegistrationDisabled)
    if (referral.props.registrationPage) {
      buildRegistrationPage(referral.props.registrationPage)
    }

    loginDisabled.set(referral.props.isLoginDisabled)
    if (_partner.origin.loginPage) {
      buildLoginPage(_partner.origin.loginPage)
    }
  }
}

function buildLoginPage(loginPage) {
  if (loginPage) {
    const {  title, subTitle, externalAuth } = loginPage

    if (title) {
      loginPageTitle.set(title)
    }
    if (subTitle) {
      loginPageSubTitle.set(subTitle)
    }

    if (externalAuth) {
      const { ctaUrl, ctaLabel } = externalAuth
      if (ctaUrl) {
        externalLoginCTAUrl.set(ctaUrl)
      }
      if (ctaLabel) {
        externalLoginCTA.set(ctaLabel)
      }
      externalLogin.set(true)
    }
  }
}

function buildRegistrationPage(registrationPage) {
  const {
    title,
    subTitle,
    showInfoBox,
    showBanner,
    infoBox
  } = registrationPage

  if (title) {
    registrationPageTitle.set(title)
  }
  if (subTitle) {
    registrationPageSubTitle.set(subTitle)
  }
  if (showInfoBox) {
    registrationPageShowInfoBox.set(showInfoBox)
  }
  if (showBanner) {
    registrationPageShowBanner.set(showBanner)
  }
  if (infoBox) {
    if (infoBox.header) {
      registrationInfoBoxHeader.set(infoBox.header)  
    }
    if (infoBox.description) {
      registrationInfoBoxDescription.set(infoBox.description)  
    }
  }
}